<template>
  <DrawerVue :refreshDisplay="refreshDisplay">
    <v-container fluid class="mt-4">
      <v-row class="mb-4" align="center">
        <v-col cols="auto">
          <h2 v-if="digitalBundleData">
            {{ $t('basics.edit', { type: $t('basics.digitalBundle') }) }}
          </h2>
          <h2 v-else>
            {{ $t('basics.new', { type: $t('basics.digitalBundle') }) }}
          </h2>
        </v-col>
        <v-spacer />
        <v-col cols="auto" v-if="digitalBundleData && $checkFunctionPermission(['digital_bundle_delete_detail'])">
          <v-tooltip :text="$t('basics.delete')">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" @click="$emit('delete')" class="icon-transparency hoverable">
                mdi-trash-can
              </v-icon>
            </template>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-form ref="digitalBundleEditForm" @submit.prevent="confirm">
        <v-row>
          <v-col cols="12">
            <v-text-field v-model="form.name" variant="outlined" flat
              :label="$t('basics.name') + '*'" :rules="[requiredRule, max256]" :readonly="readonly" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea v-model="form.description" variant="outlined" flat
              :label="$t('basics.description')" :readonly="readonly" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field v-model="form.price" variant="outlined" flat type="number"
              step="0.01" min="0" max="99999.99"
              :label="$t('basics.price') + (isSubscription ? ' / ' + $t('basics.month') : '') + '*'"
              :rules="[requiredRule, priceRule]" :readonly="readonly" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-autocomplete v-model="form.digital_medias" :items="media"
              item-title="name" item-value="id" multiple chips closable-chips variant="outlined" flat
              :label="$t('basics.digitalMedias')" :readonly="readonly"
              :loading="loading" :disabled="loading || loadFailed" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-radio-group v-model="form.payment_type" :readonly="readonly"
              :disabled="loading || loadFailed" :rules="[requiredRule]"
              color="primary" inline>
              <v-radio :label="$t('basics.oneTimePayment')" :value="paymentTypeOneTime" />
              <v-radio :label="$t('basics.monthlySubscription')" :value="paymentTypeSubscription" />
            </v-radio-group>
            <small v-if="isSubscription">{{ $t('messages.monthlySubscriptionHint') }}</small>
          </v-col>
        </v-row>
        <v-row v-if="loadFailed">
          <v-col cols="12">
            <v-alert type="error">{{ $t('messages.bundleLoadFailed') }}</v-alert>
          </v-col>
        </v-row>
        <v-row v-if="!readonly">
          <v-col cols="12">
            <v-btn type="submit" large block flat class="primaryButton"
              :loading="saving" :disabled="loading || loadFailed || saving" style="width: 100%">
              <span>{{ $t('basics.confirm') }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </DrawerVue>
</template>

<script>
// Values from the supplied app_media/models.py: one_time=0, subscription=1, monthly=0.
const PAYMENT_TYPE_ONE_TIME = 0;
const PAYMENT_TYPE_SUBSCRIPTION = 1;
const SUBSCRIPTION_CYCLE_MONTHLY = 0;
const emptyForm = () => ({
  name: '',
  description: '',
  price: '',
  payment_type: PAYMENT_TYPE_ONE_TIME,
  subscription_cycle: SUBSCRIPTION_CYCLE_MONTHLY,
  digital_medias: [],
});

export default {
  name: 'digitalBundleEditDrawer',
  props: ['digitalBundleData', 'refreshDisplay', 'readonly'],
  emits: ['close', 'delete'],
  data() {
    return {
      form: emptyForm(),
      media: [],
      loading: false,
      saving: false,
      loadFailed: false,
      loadId: 0,
      paymentTypeOneTime: PAYMENT_TYPE_ONE_TIME,
      paymentTypeSubscription: PAYMENT_TYPE_SUBSCRIPTION,
      requiredRule: value =>
        (value !== null && value !== undefined && value !== '') || this.$i18n.t('messages.required'),
      max256: value =>
        !value || value.length <= 256 || this.$i18n.t('messages.tooLong', { count: 256 }),
      priceRule: value =>
        (Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 99999.99 &&
          /^\d+(\.\d{1,2})?$/.test(String(value))) || this.$i18n.t('messages.invalidPrice'),
    };
  },
  computed: {
    isSubscription() {
      return this.form.payment_type === PAYMENT_TYPE_SUBSCRIPTION;
    },
  },
  watch: {
    refreshDisplay(value) {
      if (value) this.open();
      else { this.loadId++; this.loading = false; }
    },
  },
  methods: {
    error(error) {
      window.dispatchEvent(new CustomEvent('showErrorDisplay', { detail: error }));
    },
    async open() {
      const id = ++this.loadId;
      const bundleId = this.digitalBundleData?.id;
      this.loading = true;
      this.loadFailed = false;
      this.form = emptyForm();
      this.media = [];
      try {
        const base = this.axios.defaults.baseURL;
        const detail = bundleId
          ? await this.axios.get(base + 'media/digitalbundles/' + bundleId)
          : null;
        const media = [];
        let url = base + 'media/digitalmedias/';
        while (url) {
          const { data } = await this.axios.get(url);
          if (id !== this.loadId) return;
          media.push(...(data.results ?? data));
          url = data.next || null;
        }
        if (id !== this.loadId) return;
        this.media = media;
        const data = detail?.data;
        if (data) {
          this.form = {
            name: data.name,
            description: data.description ?? '',
            price: data.price,
            payment_type: data.payment_type,
            subscription_cycle: data.subscription_cycle,
            digital_medias: [...(data.digital_medias || [])],
          };
          const known = new Set(media.map(item => item.id));
          this.form.digital_medias.filter(mediaId => !known.has(mediaId)).forEach(mediaId => {
            this.media.push({ id: mediaId, name: this.$t('messages.digitalMediaWithId', { id: mediaId }) });
          });
        }
        this.$nextTick(() => {
          if (id === this.loadId) this.$refs.digitalBundleEditForm?.resetValidation();
        });
      } catch (error) {
        if (id === this.loadId) { this.loadFailed = true; this.error(error); }
      } finally {
        if (id === this.loadId) this.loading = false;
      }
    },
    async confirm() {
      if (this.saving || this.loading || this.loadFailed || this.readonly) return;
      const { valid } = await this.$refs.digitalBundleEditForm.validate();
      if (!valid || this.saving) return;
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          price: Number(this.form.price).toFixed(2),
          subscription_cycle: SUBSCRIPTION_CYCLE_MONTHLY,
          digital_medias: [...this.form.digital_medias],
        };
        const base = this.axios.defaults.baseURL + 'media/digitalbundles/';
        if (this.digitalBundleData) await this.axios.patch(base + 'patch/' + this.digitalBundleData.id, payload);
        else await this.axios.post(base + 'post/', payload);
        this.$emit('close', 1);
      } catch (error) {
        this.error(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
